import { useQuery } from '@apollo/client';
import { MY_BENEFITS } from '@src/graphql/queries/benefits';
import { useAuth } from '@src/hooks/useAuth';
import { RuleType } from '@src/types';
import { sanitizePaymentRequestForSpec } from '@src/utilities/paymentRequestHelper';
import { useEffect, useMemo, useRef } from 'react';
export const PROGRAM_BENEFIT_FIELDS = [
    'id',
    'clientId',
    'fieldOfStudyId',
    'schoolId',
    'programLevelId',
];
export const useMyBenefits = ({ program, paymentRequest, skip } = {}) => {
    const { currentUser } = useAuth();
    const benefitProgramProps = useMemo(() => {
        if (program?.id === undefined) {
            return undefined;
        }
        const programProps = {
            id: program?.id,
            clientId: program?.clientId ?? 0,
            fieldOfStudyId: program?.fieldOfStudyId,
            schoolId: program?.schoolId,
            programLevelId: program?.programLevelId,
        };
        if (!programProps.programLevelId) {
            programProps.programLevelId = null; // set to null so that it gets passed to the backend
        }
        return programProps;
    }, [
        // PROGRAM_BENEFIT_FIELDS is used to determine if the program has changed.
        program?.id,
        program?.clientId,
        program?.fieldOfStudyId,
        program?.schoolId,
        program?.programLevelId,
    ]);
    const spec = useMemo(() => {
        return {
            program: benefitProgramProps,
            paymentRequest: paymentRequest
                ? sanitizePaymentRequestForSpec(paymentRequest)
                : undefined,
        };
    }, [benefitProgramProps, paymentRequest]);
    const { data, error, loading, refetch } = useQuery(MY_BENEFITS, {
        variables: {
            spec,
        },
        skip: !currentUser || skip,
    });
    const myBenefits = data?.myBenefits;
    const lastKnownBenefits = useRef(undefined);
    useEffect(() => {
        if (myBenefits) {
            lastKnownBenefits.current = myBenefits;
        }
    }, [myBenefits]);
    // Eligibility can be got from any of the programs
    const isEligible = myBenefits?.eligibilities?.[0]?.isEligible ?? false;
    const getPolicy = (ruleType, program) => {
        let policies = [];
        switch (ruleType) {
            case RuleType.PROGRAM:
                policies = myBenefits?.programs ?? [];
                break;
            case RuleType.FUNDING:
                policies = myBenefits?.fundings ?? [];
                break;
            case RuleType.ELIGIBILITY:
                policies = myBenefits?.eligibilities ?? [];
                break;
            case RuleType.APPROVAL_CHAIN:
                policies = myBenefits?.approvalChains ?? [];
                break;
            case RuleType.SCHOOL:
                policies = myBenefits?.schools ?? [];
                break;
            case RuleType.EXPENSE:
                policies = myBenefits?.expenses ?? [];
                break;
            case RuleType.ACADEMIC:
                policies = myBenefits?.academics ?? [];
                break;
            case RuleType.BUDGET:
                policies = myBenefits?.budgets ?? [];
                break;
        }
        // console.log('getPolicy', ruleType, program, policies);
        return policies.find(policy => policy.program?.id === program?.id);
    };
    const getMaxFunding = () => {
        let maxCap = 0;
        let maxFunding;
        for (const funding of myBenefits?.fundings ?? []) {
            if (Number(funding.cap) > Number(maxCap)) {
                maxCap = funding.cap;
                maxFunding = funding;
            }
        }
        return maxFunding;
    };
    return {
        myBenefits: loading ? lastKnownBenefits.current : myBenefits,
        error,
        loading,
        isEligible,
        getPolicy,
        getMaxFunding,
        refetch,
        benefitProgramProps,
    };
};
